import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import type {
  Campaign,
  CampaignChannel,
  CampaignStatus,
  ChannelMix,
  CreateCampaign,
  DayOfWeekHeatmap,
  HeatmapCell,
  LeadSourceAttribution,
  LsaLead,
  LsaSummary,
  MarketingHeadline,
  OutboundFunnel,
  OutboundFunnelRow,
  OutreachLogRow,
  PatchCampaign,
} from "@breeze/shared";
import { TopNav } from "./TopNav.js";
import { Section } from "./BusinessGrowth.js";
import {
  createCampaign,
  deleteCampaign,
  fetchCampaigns,
  fetchChannelMix,
  fetchHeatmap,
  fetchLeadSourceAttribution,
  fetchLsaLeads,
  fetchLsaSummary,
  fetchMarketingHeadline,
  fetchOutboundFunnel,
  fetchOutreachLog,
  patchCampaign,
  syncLsa,
} from "../api.js";

// ===========================================================================
// Marketing dashboard.
//
// Phase 1 widgets (read-only):
//   - Headline strip (sends, reply rate, bookings, revenue)
//   - Outbound funnel — per-campaign sends → replied → booked → revenue
//   - Channel mix — outbound × channel × AI/human
//   - Day-of-week × hour heatmap of reply rate
//   - Lead-source attribution — per-month outbound vs inbound vs google_ads
//   - Recent outreach log
// Phase 2 (writeable):
//   - Campaign register w/ inline create + status toggle
//   - Detail panel surfacing the funnel rollup when outreachKey is set
//
// Design rules (memory):
//   - text-ink-50 cream for readable text (no aubergine for body text)
//   - dark mode (bg-ink-800)
//   - large minimum font sizes
//   - one font (Open Sans)
// ===========================================================================

type Props = { userName: string };

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function thisMonthIso(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

function shiftMonth(month: string, delta: number): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
}

function monthLabel(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const names = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${names[Number(m[2]) - 1]} ${m[1]}`;
}

function monthRange(month: string): { start: string; end: string } {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) {
    const today = new Date().toISOString().slice(0, 10);
    return { start: today, end: today };
  }
  const year = Number(m[1]);
  const mon = Number(m[2]);
  const start = `${m[1]}-${m[2]}-01`;
  const lastDay = new Date(Date.UTC(year, mon, 0)).getUTCDate();
  const end = `${m[1]}-${m[2]}-${pad(lastDay)}`;
  return { start, end };
}

function formatGBP(amount: number, opts: { decimals?: 0 | 2 } = {}): string {
  const decimals = opts.decimals ?? 2;
  const abs = Math.abs(amount).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return amount < 0 ? `−£${abs}` : `£${abs}`;
}

function formatPct(fraction: number, decimals = 1): string {
  return `${(fraction * 100).toFixed(decimals)}%`;
}

function formatDelta(pct: number | null): { text: string; tone: "good" | "bad" | "neutral" } {
  if (pct === null) return { text: "—", tone: "neutral" };
  const sign = pct > 0 ? "+" : "";
  const tone = pct > 0 ? "good" : pct < 0 ? "bad" : "neutral";
  return { text: `${sign}${pct.toFixed(1)}%`, tone };
}

const PALETTE = [
  "#36C5F0", "#ECB22E", "#F07B3F", "#7F6DC5",
  "#2EB67D", "#E01E5A", "#A678A8", "#611F69",
];

const CHANNEL_COLOR: Record<string, string> = {
  email: "#36C5F0",
  sms: "#ECB22E",
  whatsapp: "#2EB67D",
  voip: "#F07B3F",
  google_ads: "#E01E5A",
  bing_ads: "#7F6DC5",
  meta_ads: "#A678A8",
  referral: "#611F69",
  organic: "#666",
  mixed: "#888",
};

const CHANNEL_LABEL: Record<string, string> = {
  email: "Email",
  sms: "SMS",
  whatsapp: "WhatsApp",
  voip: "Phone",
  google_ads: "Google Ads",
  bing_ads: "Bing Ads",
  meta_ads: "Meta Ads",
  referral: "Referral",
  organic: "Organic",
  mixed: "Mixed",
};

const CAMPAIGN_STATUSES: CampaignStatus[] = [
  "planned", "running", "paused", "completed", "cancelled",
];

const CAMPAIGN_CHANNELS: CampaignChannel[] = [
  "email", "sms", "whatsapp", "voip",
  "google_ads", "bing_ads", "meta_ads",
  "referral", "organic", "mixed",
];

const STATUS_TONE: Record<CampaignStatus, string> = {
  planned: "bg-ink-600 text-ink-50",
  running: "bg-stage-won/20 text-stage-won",
  paused: "bg-stage-opportunity/20 text-stage-opportunity",
  completed: "bg-stage-lead/20 text-stage-lead",
  cancelled: "bg-stage-lost/20 text-stage-lost",
};

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function Marketing({ userName }: Props): JSX.Element {
  const [month, setMonth] = useState<string>(thisMonthIso());
  const periodDays = 90;
  const trendMonths = 6;

  return (
    <div className="flex h-full flex-col bg-ink-800">
      <TopNav userName={userName} />
      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
                Marketing
              </div>
              <h1 className="mt-1 text-2xl font-semibold text-ink-50">
                {monthLabel(month)}
              </h1>
            </div>
            <MonthPicker month={month} onChange={setMonth} />
          </div>

          <HeadlineSection month={month} />

          <LsaPerformanceSection days={periodDays} />

          <OutboundFunnelSection month={month} />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ChannelMixSection days={periodDays} />
            <LeadSourceAttributionSection months={trendMonths} />
          </div>

          <DayOfWeekHeatmapSection days={periodDays} />

          <CampaignsSection />

          <OutreachLogSection />
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month picker
// ---------------------------------------------------------------------------

function MonthPicker({
  month,
  onChange,
}: {
  month: string;
  onChange: (m: string) => void;
}): JSX.Element {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  const year = m ? Number(m[1]) : new Date().getFullYear();
  const mon = m ? Number(m[2]) : new Date().getMonth() + 1;
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const years = useMemo(() => {
    const now = new Date().getUTCFullYear();
    const arr: number[] = [];
    for (let y = now; y >= 2024; y--) arr.push(y);
    return arr;
  }, []);
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onChange(shiftMonth(month, -1))}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50 hover:bg-ink-600"
        title="Previous month"
      >
        ←
      </button>
      <select
        value={mon}
        onChange={(e) => onChange(`${year}-${pad(Number(e.target.value))}`)}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
      >
        {months.map((label, i) => (
          <option key={i} value={i + 1}>{label}</option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => onChange(`${e.target.value}-${pad(mon)}`)}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
      >
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
      <button
        onClick={() => onChange(shiftMonth(month, 1))}
        disabled={month >= thisMonthIso()}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50 hover:bg-ink-600 disabled:opacity-40"
        title="Next month"
      >
        →
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Headline
// ---------------------------------------------------------------------------

function HeadlineSection({ month }: { month: string }): JSX.Element {
  const q = useQuery<MarketingHeadline>({
    queryKey: ["marketing", "headline", month],
    queryFn: () => fetchMarketingHeadline(month),
  });
  return (
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <HeadlineTile
        label="Outbound sends"
        value={q.data ? q.data.outboundSends.current.toLocaleString() : "—"}
        loading={q.isLoading}
        deltaMonth={q.data?.outboundSends.prevMonthDeltaPct ?? null}
        deltaYear={q.data?.outboundSends.prevYearDeltaPct ?? null}
      />
      <HeadlineTile
        label="Reply rate (7d)"
        value={q.data ? formatPct(q.data.replyRate.current) : "—"}
        loading={q.isLoading}
        deltaMonth={q.data?.replyRate.prevMonthDeltaPct ?? null}
        deltaYear={q.data?.replyRate.prevYearDeltaPct ?? null}
      />
      <HeadlineTile
        label="Bookings (attrib.)"
        value={q.data ? String(q.data.bookingsAttributed.current) : "—"}
        loading={q.isLoading}
        deltaMonth={q.data?.bookingsAttributed.prevMonthDeltaPct ?? null}
        deltaYear={q.data?.bookingsAttributed.prevYearDeltaPct ?? null}
      />
      <HeadlineTile
        label="Revenue (attrib.)"
        value={q.data ? formatGBP(q.data.revenueAttributed.current, { decimals: 0 }) : "—"}
        loading={q.isLoading}
        deltaMonth={q.data?.revenueAttributed.prevMonthDeltaPct ?? null}
        deltaYear={q.data?.revenueAttributed.prevYearDeltaPct ?? null}
      />
    </section>
  );
}

function HeadlineTile({
  label,
  value,
  loading,
  deltaMonth,
  deltaYear,
}: {
  label: string;
  value: string;
  loading: boolean;
  deltaMonth: number | null;
  deltaYear: number | null;
}): JSX.Element {
  const dm = formatDelta(deltaMonth);
  const dy = formatDelta(deltaYear);
  const toneCls = (t: "good" | "bad" | "neutral"): string =>
    t === "good"
      ? "text-stage-won"
      : t === "bad"
      ? "text-stage-lost"
      : "text-ink-100";
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-700 p-5 shadow-card">
      <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold tabular-nums text-ink-50">
        {loading ? "…" : value}
      </div>
      <div className="mt-3 flex items-baseline gap-3 text-[13px]">
        <span className={`${toneCls(dm.tone)} tabular-nums`}>{dm.text}</span>
        <span className="text-ink-100">vs last month</span>
      </div>
      <div className="mt-1 flex items-baseline gap-3 text-[13px]">
        <span className={`${toneCls(dy.tone)} tabular-nums`}>{dy.text}</span>
        <span className="text-ink-100">vs same month last year</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LSA performance
// ---------------------------------------------------------------------------

function LsaPerformanceSection({ days }: { days: number }): JSX.Element {
  const qc = useQueryClient();
  const summary = useQuery<LsaSummary>({
    queryKey: ["marketing", "lsa", "summary", days],
    queryFn: () => fetchLsaSummary(days),
  });
  const leads = useQuery<LsaLead[]>({
    queryKey: ["marketing", "lsa", "leads"],
    queryFn: () => fetchLsaLeads(20),
  });
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncMsg, setLastSyncMsg] = useState<string | null>(null);

  async function handleSync(): Promise<void> {
    setSyncing(true);
    setSyncError(null);
    setLastSyncMsg(null);
    try {
      const r = await syncLsa(days);
      setLastSyncMsg(`Pulled ${r.pulled} leads (${r.start} → ${r.end})`);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["marketing", "lsa"] }),
      ]);
    } catch (err) {
      setSyncError((err as Error).message);
    } finally {
      setSyncing(false);
    }
  }

  return (
    <Section
      title="Local Service Ads"
      subtitle={`Google LSA · last ${days} days · ${summary.data?.lastSyncedAt ? "last synced " + summary.data.lastSyncedAt.replace("T", " ").slice(0, 16) : "never synced"}`}
      right={
        <button
          onClick={handleSync}
          disabled={syncing}
          className="rounded-md bg-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
        >
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      }
    >
      <div className="space-y-4">
        {syncError && (
          <div className="rounded-md border border-stage-lost/40 bg-stage-lost/10 p-2 text-[13px] text-stage-lost">
            Sync failed: {syncError}
          </div>
        )}
        {lastSyncMsg && !syncError && (
          <div className="rounded-md border border-stage-won/40 bg-stage-won/10 p-2 text-[13px] text-stage-won">
            {lastSyncMsg}
          </div>
        )}
        {summary.isLoading ? (
          <Loading />
        ) : !summary.data ? (
          <Empty />
        ) : (
          <LsaTiles s={summary.data} />
        )}
        {leads.isLoading ? null : !leads.data || leads.data.length === 0 ? (
          <div className="text-[15px] text-ink-100">
            No leads yet — once Google starts serving impressions and someone calls/messages, leads land here.
          </div>
        ) : (
          <LsaLeadsTable rows={leads.data} />
        )}
      </div>
    </Section>
  );
}

function LsaTiles({ s }: { s: LsaSummary }): JSX.Element {
  const chargedPct = s.leads > 0 ? s.chargedLeads / s.leads : 0;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
      <MiniTile label="Leads received" value={String(s.leads)} hint={`${s.phoneLeads}📞 ${s.messageLeads}💬 ${s.bookingLeads}📅`} />
      <MiniTile label="Charged" value={String(s.chargedLeads)} hint={`${formatPct(chargedPct, 0)} of total`} />
      <MiniTile label="Spend" value={formatGBP(s.spendGbp, { decimals: 0 })} />
      <MiniTile
        label="Cost / charged lead"
        value={s.costPerChargedLead !== null ? formatGBP(s.costPerChargedLead, { decimals: 2 }) : "—"}
      />
      <MiniTile
        label="Disputed"
        value={String(s.disputed)}
        hint={s.disputed > 0 ? "follow up in LSA UI" : "all clear"}
      />
    </div>
  );
}

function MiniTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-800/60 p-3">
      <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-ink-50">{value}</div>
      {hint && <div className="mt-1 text-[12px] text-ink-100">{hint}</div>}
    </div>
  );
}

const LSA_TYPE_ICON: Record<string, string> = {
  PHONE_CALL: "📞",
  MESSAGE: "💬",
  BOOKING: "📅",
  UNKNOWN: "·",
};

function LsaLeadsTable({ rows }: { rows: LsaLead[] }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-[15px] text-ink-50">
        <thead>
          <tr className="text-left text-[13px] uppercase tracking-[0.14em] text-ink-100">
            <th className="py-2 pr-3">Received</th>
            <th className="py-2 px-3">Type</th>
            <th className="py-2 px-3">Consumer</th>
            <th className="py-2 px-3">Phone</th>
            <th className="py-2 px-3">Charged?</th>
            <th className="py-2 px-3 text-right">Cost</th>
            <th className="py-2 pl-3">Dispute</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-700">
          {rows.map((r) => {
            const name = [r.consumerFirstName, r.consumerLastName].filter(Boolean).join(" ") || "—";
            const charged = r.chargeStatus === "CHARGED";
            return (
              <tr key={r.leadId}>
                <td className="py-2 pr-3 text-[13px] text-ink-100 whitespace-nowrap">
                  {r.leadCreationAt.replace("T", " ").slice(0, 16)}
                </td>
                <td className="py-2 px-3">
                  {LSA_TYPE_ICON[r.leadType] ?? "·"} {r.leadType.toLowerCase().replace("_", " ")}
                </td>
                <td className="py-2 px-3">{name}</td>
                <td className="py-2 px-3 font-mono text-[13px]">{r.consumerPhone ?? "—"}</td>
                <td className="py-2 px-3">
                  {charged ? (
                    <span className="text-stage-won">✓</span>
                  ) : (
                    <span className="text-ink-100">—</span>
                  )}
                </td>
                <td className="py-2 px-3 text-right font-semibold tabular-nums">
                  {r.chargeAmountGbp !== null ? formatGBP(r.chargeAmountGbp, { decimals: 2 }) : "—"}
                </td>
                <td className="py-2 pl-3 text-[13px]">
                  {r.disputeStatus && r.disputeStatus !== "NO_DISPUTE" ? (
                    <span className="text-stage-lost">{r.disputeStatus}</span>
                  ) : (
                    <span className="text-ink-100">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outbound funnel
// ---------------------------------------------------------------------------

function OutboundFunnelSection({ month }: { month: string }): JSX.Element {
  const { start, end } = monthRange(month);
  const q = useQuery<OutboundFunnel>({
    queryKey: ["marketing", "funnel", start, end],
    queryFn: () => fetchOutboundFunnel(start, end),
  });
  return (
    <Section
      title="Outbound funnel"
      subtitle={`Sends → replies → bookings → revenue · ${monthLabel(month)}`}
    >
      {q.isLoading ? (
        <Loading />
      ) : !q.data || q.data.rows.length === 0 ? (
        <Empty message="No outbound sends in this window." />
      ) : (
        <FunnelTable rows={q.data.rows} />
      )}
    </Section>
  );
}

function FunnelTable({ rows }: { rows: OutboundFunnelRow[] }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-[15px] text-ink-50">
        <thead>
          <tr className="text-left text-[13px] uppercase tracking-[0.14em] text-ink-100">
            <th className="py-2 pr-3">Campaign</th>
            <th className="py-2 px-3">Channel</th>
            <th className="py-2 px-3 text-right">Sends</th>
            <th className="py-2 px-3 text-right">Replied</th>
            <th className="py-2 px-3 text-right">Reply %</th>
            <th className="py-2 px-3 text-right">Booked</th>
            <th className="py-2 px-3 text-right">Book %</th>
            <th className="py-2 pl-3 text-right">Revenue</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-700">
          {rows.map((r) => {
            const replyPct = r.sends > 0 ? r.replied / r.sends : 0;
            const bookPct = r.sends > 0 ? r.booked / r.sends : 0;
            return (
              <tr key={r.campaignName}>
                <td className="py-3 pr-3 font-semibold">{r.campaignName}</td>
                <td className="py-3 px-3">
                  <span
                    className="inline-flex items-center gap-2 rounded-md px-2 py-0.5 text-[13px]"
                    style={{
                      background: `${CHANNEL_COLOR[r.channel] ?? "#444"}33`,
                      color: CHANNEL_COLOR[r.channel] ?? "#ccc",
                    }}
                  >
                    {CHANNEL_LABEL[r.channel] ?? r.channel}
                  </span>
                </td>
                <td className="py-3 px-3 text-right tabular-nums">{r.sends.toLocaleString()}</td>
                <td className="py-3 px-3 text-right tabular-nums">{r.replied}</td>
                <td className="py-3 px-3 text-right tabular-nums text-ink-100">
                  {formatPct(replyPct, 0)}
                </td>
                <td className="py-3 px-3 text-right tabular-nums">{r.booked}</td>
                <td className="py-3 px-3 text-right tabular-nums text-ink-100">
                  {formatPct(bookPct, 1)}
                </td>
                <td className="py-3 pl-3 text-right font-semibold tabular-nums">
                  {formatGBP(r.revenue, { decimals: 0 })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Channel mix
// ---------------------------------------------------------------------------

function ChannelMixSection({ days }: { days: number }): JSX.Element {
  const q = useQuery<ChannelMix>({
    queryKey: ["marketing", "channel-mix", days],
    queryFn: () => fetchChannelMix(days),
  });
  return (
    <Section title="Channel mix" subtitle={`Outbound by channel · last ${days} days`}>
      {q.isLoading ? (
        <Loading />
      ) : !q.data || q.data.rows.length === 0 ? (
        <Empty />
      ) : (
        <ChannelMixDonut mix={q.data} />
      )}
    </Section>
  );
}

function ChannelMixDonut({ mix }: { mix: ChannelMix }): JSX.Element {
  const out = mix.rows.filter((r) => r.direction === "out");
  if (out.length === 0) return <Empty message="No outbound in this window." />;
  const data = out.map((r) => ({
    name: CHANNEL_LABEL[r.channel] ?? r.channel,
    value: r.count,
    aiCount: r.agentCount,
    color: CHANNEL_COLOR[r.channel] ?? "#888",
  }));
  const total = data.reduce((s, d) => s + d.value, 0);
  const totalAi = data.reduce((s, d) => s + d.aiCount, 0);
  const aiPct = total > 0 ? totalAi / total : 0;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={50}
              outerRadius={90}
              paddingAngle={2}
            >
              {data.map((d, i) => (
                <Cell key={i} fill={d.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: "#221F27",
                border: "1px solid #4A4358",
                color: "#FFFFFF",
              }}
              formatter={(value, _name, item) => {
                const v = Number(value ?? 0);
                const ai = (item?.payload as { aiCount?: number } | undefined)?.aiCount ?? 0;
                return [`${v.toLocaleString()} (${ai} AI)`, "Sent"];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-2 text-[15px] text-ink-50">
        <div className="flex items-baseline justify-between rounded-md bg-ink-800 px-3 py-2">
          <span>Total outbound</span>
          <span className="font-semibold tabular-nums">{total.toLocaleString()}</span>
        </div>
        <div className="flex items-baseline justify-between rounded-md bg-ink-800 px-3 py-2">
          <span>AI-driven</span>
          <span className="font-semibold tabular-nums text-stage-won">
            {totalAi.toLocaleString()} ({formatPct(aiPct, 0)})
          </span>
        </div>
        <ul className="space-y-1">
          {data.map((d) => (
            <li key={d.name} className="flex items-baseline justify-between">
              <span className="flex items-center gap-2">
                <span
                  className="inline-block h-3 w-3 rounded-sm"
                  style={{ background: d.color }}
                />
                <span>{d.name}</span>
              </span>
              <span className="font-semibold tabular-nums">
                {d.value.toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lead-source attribution (per-month stacked)
// ---------------------------------------------------------------------------

function LeadSourceAttributionSection({ months }: { months: number }): JSX.Element {
  const q = useQuery<LeadSourceAttribution>({
    queryKey: ["marketing", "attribution", months],
    queryFn: () => fetchLeadSourceAttribution(months),
  });
  return (
    <Section title="Lead source attribution" subtitle={`Revenue split · last ${months} months`}>
      {q.isLoading ? (
        <Loading />
      ) : !q.data || q.data.months.length === 0 ? (
        <Empty />
      ) : (
        <AttributionChart data={q.data} />
      )}
    </Section>
  );
}

function AttributionChart({ data }: { data: LeadSourceAttribution }): JSX.Element {
  const rows = data.months.map((m) => ({
    month: monthLabel(m.month),
    Outbound: m.outbound.revenue,
    Inbound: m.inbound.revenue,
    "Google Ads": m.googleAds.revenue,
  }));
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#332D3A" strokeDasharray="3 3" />
          <XAxis dataKey="month" stroke="#F0F0F0" tick={{ fill: "#F0F0F0", fontSize: 13 }} />
          <YAxis
            stroke="#F0F0F0"
            tick={{ fill: "#F0F0F0", fontSize: 13 }}
            tickFormatter={(v: number) => formatGBP(v, { decimals: 0 })}
          />
          <Tooltip
            contentStyle={{
              background: "#221F27",
              border: "1px solid #4A4358",
              color: "#FFFFFF",
            }}
            formatter={(value, name) => [formatGBP(Number(value ?? 0), { decimals: 0 }), String(name ?? "")]}
          />
          <Legend wrapperStyle={{ color: "#F0F0F0" }} />
          <Bar dataKey="Outbound" stackId="rev" fill="#2EB67D" />
          <Bar dataKey="Inbound" stackId="rev" fill="#36C5F0" />
          <Bar dataKey="Google Ads" stackId="rev" fill="#E01E5A" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Day-of-week × hour heatmap
// ---------------------------------------------------------------------------

function DayOfWeekHeatmapSection({ days }: { days: number }): JSX.Element {
  const q = useQuery<DayOfWeekHeatmap>({
    queryKey: ["marketing", "heatmap", days],
    queryFn: () => fetchHeatmap(days),
  });
  return (
    <Section
      title="Reply rate heatmap"
      subtitle={`Outbound × hour-of-week · last ${days} days · greener = higher reply rate`}
    >
      {q.isLoading ? (
        <Loading />
      ) : !q.data || q.data.cells.length === 0 ? (
        <Empty />
      ) : (
        <Heatmap cells={q.data.cells} />
      )}
    </Section>
  );
}

function Heatmap({ cells }: { cells: HeatmapCell[] }): JSX.Element {
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const byKey = new Map<string, HeatmapCell>();
  for (const c of cells) byKey.set(`${c.dayOfWeek}:${c.hour}`, c);
  const maxRate = cells.reduce(
    (mx, c) => (c.replyRate !== null && c.replyRate > mx ? c.replyRate : mx),
    0
  );

  function cellColor(c: HeatmapCell): string {
    if (c.sends === 0 || c.replyRate === null) return "#221F27";
    if (maxRate === 0) return "#1d2429";
    const t = Math.min(1, c.replyRate / maxRate);
    // green gradient — interpolate between dim ink and stage-won
    const g = Math.round(48 + 134 * t);
    const r = Math.round(34 + 12 * t);
    const b = Math.round(40 + 85 * t);
    return `rgb(${r}, ${g}, ${b})`;
  }

  return (
    <div className="overflow-x-auto">
      <div className="inline-block">
        <div className="flex items-center gap-1 pl-12">
          {Array.from({ length: 24 }, (_, h) => (
            <div
              key={h}
              className="w-6 text-center text-[11px] tabular-nums text-ink-100"
            >
              {h % 2 === 0 ? h : ""}
            </div>
          ))}
        </div>
        {dayNames.map((dn, i) => (
          <div key={dn} className="mt-1 flex items-center gap-1">
            <div className="w-10 text-right text-[13px] text-ink-100">{dn}</div>
            <div className="w-1" />
            {Array.from({ length: 24 }, (_, h) => {
              const c = byKey.get(`${i + 1}:${h}`);
              const sends = c?.sends ?? 0;
              const rate = c?.replyRate ?? null;
              const title =
                sends === 0
                  ? `${dn} ${h}:00 — no sends`
                  : `${dn} ${h}:00 — ${sends} sent · ${rate !== null ? formatPct(rate, 0) : "—"} reply`;
              return (
                <div
                  key={h}
                  className="h-6 w-6 rounded-sm border border-ink-800"
                  style={{ background: c ? cellColor(c) : "#221F27" }}
                  title={title}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campaigns (Phase 2)
// ---------------------------------------------------------------------------

function CampaignsSection(): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery<Campaign[]>({
    queryKey: ["marketing", "campaigns"],
    queryFn: () => fetchCampaigns(),
  });
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);

  return (
    <Section
      title="Campaigns"
      subtitle="Plan, run, and review outbound + paid campaigns"
      right={
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-md bg-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-aubergineLight"
        >
          + New campaign
        </button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : !q.data || q.data.length === 0 ? (
        <Empty message="No campaigns yet. Click + New campaign to add one." />
      ) : (
        <CampaignList rows={q.data} onEdit={(c) => setEditing(c)} />
      )}
      {showCreate && (
        <CampaignFormModal
          mode="create"
          initial={null}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ["marketing", "campaigns"] });
            setShowCreate(false);
          }}
        />
      )}
      {editing && (
        <CampaignFormModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ["marketing", "campaigns"] });
            setEditing(null);
          }}
        />
      )}
    </Section>
  );
}

function CampaignList({
  rows,
  onEdit,
}: {
  rows: Campaign[];
  onEdit: (c: Campaign) => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-[15px] text-ink-50">
        <thead>
          <tr className="text-left text-[13px] uppercase tracking-[0.14em] text-ink-100">
            <th className="py-2 pr-3">Name</th>
            <th className="py-2 px-3">Channel</th>
            <th className="py-2 px-3">Status</th>
            <th className="py-2 px-3">Dates</th>
            <th className="py-2 px-3 text-right">Sends</th>
            <th className="py-2 px-3 text-right">Booked</th>
            <th className="py-2 px-3 text-right">Revenue</th>
            <th className="py-2 px-3 text-right">Cost</th>
            <th className="py-2 pl-3 text-right">ROI</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-700">
          {rows.map((c) => {
            const cost = c.actualCostGbp ?? 0;
            const revenue = c.rollup?.revenue ?? 0;
            const roi = cost > 0 ? revenue / cost : null;
            return (
              <tr
                key={c.id}
                onClick={() => onEdit(c)}
                className="cursor-pointer hover:bg-ink-700/60"
              >
                <td className="py-3 pr-3 font-semibold">{c.name}</td>
                <td className="py-3 px-3">
                  <span
                    className="inline-flex items-center rounded-md px-2 py-0.5 text-[13px]"
                    style={{
                      background: `${CHANNEL_COLOR[c.channel] ?? "#444"}33`,
                      color: CHANNEL_COLOR[c.channel] ?? "#ccc",
                    }}
                  >
                    {CHANNEL_LABEL[c.channel] ?? c.channel}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[13px] ${STATUS_TONE[c.status]}`}>
                    {c.status}
                  </span>
                </td>
                <td className="py-3 px-3 text-[13px] text-ink-100">
                  {c.startsOn ?? "—"}
                  {c.endsOn ? ` → ${c.endsOn}` : ""}
                </td>
                <td className="py-3 px-3 text-right tabular-nums">
                  {c.rollup ? c.rollup.sends.toLocaleString() : "—"}
                </td>
                <td className="py-3 px-3 text-right tabular-nums">
                  {c.rollup ? c.rollup.booked : "—"}
                </td>
                <td className="py-3 px-3 text-right font-semibold tabular-nums">
                  {c.rollup ? formatGBP(c.rollup.revenue, { decimals: 0 }) : "—"}
                </td>
                <td className="py-3 px-3 text-right tabular-nums">
                  {c.actualCostGbp !== null ? formatGBP(c.actualCostGbp, { decimals: 0 }) : "—"}
                </td>
                <td className="py-3 pl-3 text-right tabular-nums font-semibold">
                  {roi !== null ? `${roi.toFixed(2)}×` : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CampaignFormModal({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  initial: Campaign | null;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [name, setName] = useState(initial?.name ?? "");
  const [status, setStatus] = useState<CampaignStatus>(initial?.status ?? "planned");
  const [channel, setChannel] = useState<CampaignChannel>(initial?.channel ?? "whatsapp");
  const [startsOn, setStartsOn] = useState(initial?.startsOn ?? "");
  const [endsOn, setEndsOn] = useState(initial?.endsOn ?? "");
  const [audienceSize, setAudienceSize] = useState(
    initial?.audienceSize !== null && initial?.audienceSize !== undefined
      ? String(initial.audienceSize)
      : ""
  );
  const [budget, setBudget] = useState(
    initial?.budgetGbp !== null && initial?.budgetGbp !== undefined ? String(initial.budgetGbp) : ""
  );
  const [actualCost, setActualCost] = useState(
    initial?.actualCostGbp !== null && initial?.actualCostGbp !== undefined
      ? String(initial.actualCostGbp)
      : ""
  );
  const [outreachKey, setOutreachKey] = useState(initial?.outreachKey ?? "");
  const [hypothesis, setHypothesis] = useState(initial?.hypothesis ?? "");
  const [retroSummary, setRetroSummary] = useState(initial?.retroSummary ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const payload: CreateCampaign = {
        name: name.trim(),
        status,
        channel,
        startsOn: startsOn || null,
        endsOn: endsOn || null,
        audienceSize: audienceSize ? Number(audienceSize) : null,
        budgetGbp: budget ? Number(budget) : null,
        actualCostGbp: actualCost ? Number(actualCost) : null,
        outreachKey: outreachKey.trim() || null,
        hypothesis: hypothesis.trim() || null,
        retroSummary: retroSummary.trim() || null,
        notes: notes.trim() || null,
      };
      if (mode === "create") {
        await createCampaign(payload);
      } else if (initial) {
        const patch: PatchCampaign = payload;
        await patchCampaign(initial.id, patch);
      }
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!initial) return;
    if (!confirm(`Delete "${initial.name}"? This cannot be undone.`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteCampaign(initial.id);
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl border border-ink-700 bg-ink-800 p-6 shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-ink-50">
            {mode === "create" ? "New campaign" : "Edit campaign"}
          </h3>
          <button
            onClick={onClose}
            className="text-[20px] text-ink-100 hover:text-ink-50"
          >
            ×
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name" full>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
              placeholder="Spring 15% offer (WA)"
            />
          </Field>
          <Field label="Channel">
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value as CampaignChannel)}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            >
              {CAMPAIGN_CHANNELS.map((c) => (
                <option key={c} value={c}>{CHANNEL_LABEL[c] ?? c}</option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as CampaignStatus)}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            >
              {CAMPAIGN_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Starts on">
            <input
              type="date"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </Field>
          <Field label="Ends on">
            <input
              type="date"
              value={endsOn}
              onChange={(e) => setEndsOn(e.target.value)}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </Field>
          <Field label="Audience size">
            <input
              type="number"
              min={0}
              value={audienceSize}
              onChange={(e) => setAudienceSize(e.target.value)}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
              placeholder="3500"
            />
          </Field>
          <Field label="Budget (£)">
            <input
              type="number"
              min={0}
              step="0.01"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </Field>
          <Field label="Actual cost (£)">
            <input
              type="number"
              min={0}
              step="0.01"
              value={actualCost}
              onChange={(e) => setActualCost(e.target.value)}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </Field>
          <Field label="Outreach key (campaign_outreach_log.campaign_name)" full>
            <input
              value={outreachKey}
              onChange={(e) => setOutreachKey(e.target.value)}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50 font-mono"
              placeholder="spring_wa_2026"
            />
          </Field>
          <Field label="Hypothesis (what do you expect?)" full>
            <textarea
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
              placeholder="Sunday-evening WA sends should out-pull weekday email"
            />
          </Field>
          <Field label="Retro summary (write after the fact)" full>
            <textarea
              value={retroSummary}
              onChange={(e) => setRetroSummary(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </Field>
          <Field label="Notes" full>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </Field>
        </div>
        {initial?.rollup && (
          <div className="mt-4 rounded-md border border-ink-700 bg-ink-900/40 p-3 text-[13px] text-ink-100">
            <div className="font-semibold uppercase tracking-[0.14em]">Auto rollup</div>
            <div className="mt-2 grid grid-cols-4 gap-3 text-[15px] text-ink-50">
              <div>{initial.rollup.sends} sends</div>
              <div>{initial.rollup.replied} replies</div>
              <div>{initial.rollup.booked} booked</div>
              <div>{formatGBP(initial.rollup.revenue, { decimals: 0 })}</div>
            </div>
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-md border border-stage-lost/40 bg-stage-lost/10 p-2 text-[13px] text-stage-lost">
            {error}
          </div>
        )}
        <div className="mt-5 flex items-center justify-between">
          <div>
            {mode === "edit" && (
              <button
                onClick={handleDelete}
                disabled={saving}
                className="rounded-md border border-stage-lost/40 px-3 py-2 text-[13px] text-stage-lost hover:bg-stage-lost/10 disabled:opacity-40"
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-ink-500 px-4 py-2 text-[15px] text-ink-50 hover:bg-ink-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="rounded-md bg-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
            >
              {saving ? "Saving…" : mode === "create" ? "Create" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  full,
  children,
}: {
  label: string;
  full?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="mb-1 block text-[13px] uppercase tracking-[0.14em] text-ink-100">
        {label}
      </label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent outreach log
// ---------------------------------------------------------------------------

function OutreachLogSection(): JSX.Element {
  const q = useQuery<OutreachLogRow[]>({
    queryKey: ["marketing", "outreach-log"],
    queryFn: () => fetchOutreachLog({ limit: 50 }),
  });
  return (
    <Section title="Recent outreach" subtitle="Last 50 sends across all campaigns">
      {q.isLoading ? (
        <Loading />
      ) : !q.data || q.data.length === 0 ? (
        <Empty />
      ) : (
        <OutreachTable rows={q.data} />
      )}
    </Section>
  );
}

function OutreachTable({ rows }: { rows: OutreachLogRow[] }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-[15px] text-ink-50">
        <thead>
          <tr className="text-left text-[13px] uppercase tracking-[0.14em] text-ink-100">
            <th className="py-2 pr-3">Sent</th>
            <th className="py-2 px-3">Campaign</th>
            <th className="py-2 px-3">Channel</th>
            <th className="py-2 px-3">Contact</th>
            <th className="py-2 px-3">Body</th>
            <th className="py-2 pl-3">Replied?</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-700">
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="py-2 pr-3 text-[13px] text-ink-100 whitespace-nowrap">
                {r.sentAt?.replace("T", " ").slice(0, 16) ?? "—"}
              </td>
              <td className="py-2 px-3 font-semibold">{r.campaignName}</td>
              <td className="py-2 px-3">
                <span
                  className="inline-flex rounded-md px-2 py-0.5 text-[13px]"
                  style={{
                    background: `${CHANNEL_COLOR[r.channel] ?? "#444"}33`,
                    color: CHANNEL_COLOR[r.channel] ?? "#ccc",
                  }}
                >
                  {CHANNEL_LABEL[r.channel] ?? r.channel}
                </span>
              </td>
              <td className="py-2 px-3">{r.contactName ?? "—"}</td>
              <td className="py-2 px-3 max-w-md truncate text-[13px] text-ink-100">
                {r.bodyPreview}
              </td>
              <td className="py-2 pl-3">
                {r.respondedAt ? (
                  <span className="text-stage-won">✓</span>
                ) : (
                  <span className="text-ink-100">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared mini-components
// ---------------------------------------------------------------------------

function Loading(): JSX.Element {
  return <div className="text-[15px] text-ink-100">Loading…</div>;
}

function Empty({ message }: { message?: string } = {}): JSX.Element {
  return (
    <div className="text-[15px] text-ink-100">{message ?? "No data for this period."}</div>
  );
}
